import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, LifeBuoy } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useMeta, eventJsonLd } from "@/lib/seo";
import { FAQ as FAQ_EN, FAQ_INTRO as FAQ_INTRO_EN, FAQ_DE, FAQ_INTRO_DE } from "@/lib/faqData";

const slug = s => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function Item({ item, open, onToggle, id }) {
  return (
    <div className="border-b border-foreground/30" data-testid={`faq-item-${id}`}>
      <button onClick={onToggle} aria-expanded={open} className="w-full text-left py-3.5 flex items-start gap-3 hover:text-primary transition-colors min-h-[44px]" data-testid={`faq-q-${id}`}>
        <span className="flex-1 font-display uppercase text-base sm:text-lg leading-snug tracking-tight">{item.q}</span>
        <ChevronDown className={`w-4 h-4 mt-1 shrink-0 text-primary transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-4 -mt-1 text-sm sm:text-base leading-relaxed text-foreground/90 whitespace-pre-line" data-testid={`faq-a-${id}`}>{item.a}</div>}
    </div>
  );
}

export default function Faq() {
  const { t, lang } = useApp();
  useMeta({ title: "FAQ", description: t("seoFaqDesc"), path: "/faq" });
  const FAQ = lang === "de" ? FAQ_DE : FAQ_EN;
  const FAQ_INTRO = lang === "de" ? FAQ_INTRO_DE : FAQ_INTRO_EN;
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const term = q.trim().toLowerCase();
  const cats = useMemo(() => FAQ.map(c => ({ ...c, items: term ? c.items.filter(i => (i.q + " " + i.a).toLowerCase().includes(term)) : c.items })).filter(c => c.items.length), [term, FAQ]);
  const total = cats.reduce((n, c) => n + c.items.length, 0);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-10" data-testid="faq-page">
      <div className="eyebrow text-primary">Soul Radar</div>
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter mt-2" data-testid="faq-title">FAQ</h1>
      <p className="mt-3 text-base text-muted-foreground max-w-2xl" data-testid="faq-intro">{FAQ_INTRO}</p>

      <label className="mt-6 flex items-center gap-2 border border-foreground px-3 h-12" data-testid="faq-search-wrap">
        <Search className="w-4 h-4 text-primary shrink-0" />
        <input value={q} onChange={e => { setQ(e.target.value); setOpen(null); }} placeholder={t("faqSearchPlaceholder")} className="flex-1 bg-transparent outline-none text-base" data-testid="faq-search" />
        {term && <span className="eyebrow !text-[10px] text-muted-foreground shrink-0" data-testid="faq-result-count">{t("nResults", { n: total })}</span>}
      </label>

      {!term && (
        <nav className="mt-6 flex flex-wrap gap-2" aria-label="Categories" data-testid="faq-categories">
          {FAQ.map(c => <a key={c.title} href={`#${slug(c.title)}`} className="gg-chip !py-1.5 hover:bg-muted transition-colors">{c.title}</a>)}
        </nav>
      )}

      {cats.map(c => (
        <section key={c.title} id={slug(c.title)} className="mt-10 scroll-mt-24" data-testid={`faq-cat-${slug(c.title)}`}>
          <h2 className="font-display uppercase text-2xl tracking-tight border-b-2 border-foreground pb-2 flex items-baseline gap-3"><span className="text-primary text-lg">{String(FAQ.indexOf(FAQ.find(x => x.title === c.title)) + 1).padStart(2, "0")}</span>{c.title}</h2>
          <div>{c.items.map(item => { const id = slug(item.q).slice(0, 60); return <Item key={id} id={id} item={item} open={open === id} onToggle={() => setOpen(open === id ? null : id)} />; })}</div>
        </section>
      ))}
      {term && total === 0 && <p className="mt-10 text-sm text-muted-foreground" data-testid="faq-empty">{t("faqNoResults")}</p>}

      <div className="mt-14 pt-6 border-t border-foreground flex flex-wrap items-center justify-between gap-3" data-testid="faq-support-cta">
        <span className="font-display uppercase text-xl flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-primary" />{t("stillNeedHelp")}</span>
        <Link to="/help" className="eyebrow text-primary hover:underline" data-testid="faq-contact-support">{t("contactSupport")} →</Link>
      </div>
    </div>
  );
}
